import React from 'react';
import { Box, createStyles, Grid, makeStyles, Paper } from '@material-ui/core';
import { ExtendedTheme, Heading, InlineText, Link, TextBlock } from 'neon-design-system';
import { baseUrls } from '../../../utils/baseUrls.utils';
import {ChannelSubscriptionType, ChannelSubscriptionWithPaymentInfo, ChannelTypes} from '../../../shared/types';
import { UpdateCardButton } from './UpdateCardButton';
import { capitalizeFirstLetter } from '../../../utils/string.utils';
import { config } from '../../../config';
import { getChannelPublisherUrl } from '../../../utils/channel.utils';
import {
	isNonChargeChannelSubscriptionType,
	isAccountChannelSubscriptionType,
	isExternalBillingChannelSubscriptionType,
	isTrialChannelSubscriptionType
} from '../../../utils/channelSubscriptions.utils';
import { formatDisplayDate } from '../../../utils/date.utils';
import { ChannelUnsubscribe } from './ChannelUnsubscribe';

const useStyles = makeStyles<ExtendedTheme>((theme: ExtendedTheme) =>
	createStyles({
		root: {
			padding: theme.spacing(4),
			backgroundColor: theme.themeColors.blueGrey[800]
		},
		channelName: {
			color: theme.themeColors.common.white,
			cursor: 'pointer'
		},
		b200: {
			color: theme.themeColors.blueGrey[200],
			'& a': { fontSize: 'inherit' }
		},
		b400: {
			color: theme.themeColors.blueGrey[400]
		},
		link: {
			color: theme.palette.error.main,

			'&:hover': {
				color: theme.palette.error.dark
			}
		},
		dot: {
			width: theme.spacing(0.5),
			height: theme.spacing(0.5),
			backgroundColor: theme.themeColors.grey[400],
			borderRadius: '50%'
		},
		lineThrough: {
			textDecoration: 'line-through'
		}
	})
);
interface ChannelSubscriptionCardProps {
	subscription: ChannelSubscriptionWithPaymentInfo;
	onUpdate: () => void;
}

const ChannelSubscriptionCard = ({ subscription, onUpdate }: ChannelSubscriptionCardProps) => {
	const classes = useStyles();
	const { channel } = subscription;
	if (!channel) return <></>;
	const channelUrl = `${baseUrls.browse.channelMain}/${channel._id}`;
	const price = `$${!subscription.pricePerMonth ? channel.price : subscription.pricePerMonth}/month*`;

	const showNextPaymentDate = () => {
		if (!subscription.nextPayment) return;

		return formatDisplayDate(subscription.nextPayment, false) ?? '';
	}

	const capitalizePaymentDetails = () => {
		if (subscription.paymentMethod.type === 'Card') {
			return capitalizeFirstLetter(subscription.paymentMethod.details);
		}

		return subscription.paymentMethod.details;
	}

	const getAutoCancelDate = () => formatDisplayDate(subscription?.cancelAt, false) ?? '';
	const isFreeChannelSubscriptions = subscription.channel.type !== ChannelTypes.premium;
	const disableSubscriptionOptions = subscription.cancelAtPeriodEnd;
	const rebatedAccount = isAccountChannelSubscriptionType(subscription.subscriptionType) && subscription.rebate;

	return (
		<Paper className={classes.root}>
			<Grid container spacing={4}>
				<Grid item xs={12}>
					<Grid container spacing={1}>
						<Grid item xs={12}>
							<Link to={channelUrl}>
								<Heading styleName="headline1" className={classes.channelName}>{channel.name}</Heading>
							</Link>
						</Grid>
						<Grid item xs={12}>
							<TextBlock styleName="footnote" className={classes.b200}>
								Published by <Link to={getChannelPublisherUrl(channel)}>{channel.owner.name}</Link>
							</TextBlock>
						</Grid>
					</Grid>
				</Grid>
				<Grid item xs={12}>
					<Grid container spacing={1}>
						{
							!isFreeChannelSubscriptions && (
								<Grid item xs={12}>
									<InlineText
										styleName="body"
										className={[
											classes.b200,
											(
												(isNonChargeChannelSubscriptionType(subscription.subscriptionType) && !subscription.pricePerMonth) ||
												rebatedAccount || isTrialChannelSubscriptionType(subscription.subscriptionType)
											) && classes.lineThrough
										].filter(n => !!n).join(' ')}
									>
										<b>{price}</b>
									</InlineText>
									{' '}
									{
										isTrialChannelSubscriptionType(subscription.subscriptionType)
											? <InlineText styleName="body" color="white"><strong>Free trial</strong></InlineText>
											: isNonChargeChannelSubscriptionType(subscription.subscriptionType) ? (
												!subscription.pricePerMonth ? <InlineText styleName="body"><b>Free subscription</b></InlineText> : <></>
											) : rebatedAccount ? (
												<>
													<InlineText styleName="body"><b>Rebated</b></InlineText>
													{' '}
													{showNextPaymentDate() &&
														<InlineText styleName="body" className={classes.b400}>next payment {showNextPaymentDate()}</InlineText>
													}
												</>
											) : (
												!subscription.cancelAtPeriodEnd && showNextPaymentDate() && !isExternalBillingChannelSubscriptionType(subscription.subscriptionType) &&
												<InlineText styleName="body" className={classes.b400}>next payment {showNextPaymentDate()}</InlineText>
											)
									}
								</Grid>
							)
						}
						{
							!isTrialChannelSubscriptionType(subscription.subscriptionType) && (
								<Grid item xs={12}>
									<InlineText styleName="footnote" className={classes.b200}>
										{
											isNonChargeChannelSubscriptionType(subscription.subscriptionType) ? (
												!subscription.pricePerMonth ? <>You have been granted free access to this Channel</> : <> </>
											) : (
												<><b>{subscription.paymentMethod.type}</b> {capitalizePaymentDetails()}</>
											)
										}
									</InlineText>
								</Grid>
							)
						}
						<Grid item xs={12}>
							<Box display="flex" alignItems="center">
								{
									config.stripePaymentsEnabled &&
									subscription.subscriptionType === ChannelSubscriptionType.Stripe &&
									!disableSubscriptionOptions &&
									<>
										<UpdateCardButton subscriptionId={subscription.subscriptionId} onStripeCardUpdated={onUpdate} />
										<Box mx={1}>
											<div className={classes.dot} />
										</Box>
									</>
								}
								<ChannelUnsubscribe subscription={subscription} onUpdate={onUpdate} />
							</Box>
						</Grid>
						{
							(subscription.cancelAtPeriodEnd || subscription.cancelAt) && (
								<Grid item xs={12}>
									<InlineText
										styleName="footnote"
										className={classes.b200}
									>
										{isTrialChannelSubscriptionType(subscription.subscriptionType) ? 'Free trial ending on ' : 'Subscription expires on '}{getAutoCancelDate()}
									</InlineText>
								</Grid>
							)
						}
						{
							!isFreeChannelSubscriptions && (
								<Grid item xs={12}>
									<InlineText
										styleName="footnote"
										className={classes.b400}
									><strong>*Price excludes applicable sales taxes</strong></InlineText>
								</Grid>
							)
						}
					</Grid>
				</Grid>
			</Grid>
		</Paper>
	);
};

export default ChannelSubscriptionCard;
